import React from 'react';
import { motion } from 'framer-motion';
import { BadgeCheckIcon, RefreshCwIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
const features = [{
  title: 'Monthly Refreshed',
  description: 'Database refreshed every 30 days with outdated contacts removed automatically.',
  icon: RefreshCwIcon,
  color: 'blue'
}, {
  title: 'Fully Compliant',
  description: 'GDPR, CCPA, and international data protection built into every layer.',
  icon: ShieldCheckIcon,
  color: 'emerald'
}, {
  title: 'Real-time Validation',
  description: 'Emails and phone numbers verified before delivery for maximum deliverability.',
  icon: ZapIcon,
  color: 'amber'
}];
function AnimatedRing({
  delay = 0
}: {
  delay?: number;
}) {
  return <svg width="160" height="160" viewBox="0 0 160 160" className="absolute -right-4 -bottom-4 opacity-[0.07]" data-id="element-2686">
      <motion.circle cx="80" cy="80" r="70" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="440" strokeDashoffset="440" initial={{
      strokeDashoffset: 440,
      rotate: 0
    }} whileInView={{
      strokeDashoffset: 44,
      rotate: 360
    }} viewport={{
      once: false
    }} transition={{
      strokeDashoffset: {
        duration: 1.5,
        delay,
        ease: 'easeOut'
      },
      rotate: {
        duration: 25,
        repeat: Infinity,
        ease: 'linear'
      }
    }} data-id="element-2687" />
    </svg>;
}
export function DataQualityCompliance() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-24" data-id="element-2688">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-14 lg:p-20 overflow-hidden" data-id="element-2689">
        {/* Top: Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-16" data-id="element-2690">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold tracking-wide mb-5 border border-emerald-100" data-id="element-2691">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-2692" />
            Enterprise-grade
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2693">
            Data <span className="text-blue-600" data-id="element-2694">quality</span> & compliance
          </h2>
          <p className="text-lg text-slate-500 max-w-xl" data-id="element-2695">
            Trust verified, up-to-date data that respects privacy regulations.
          </p>
        </motion.div>

        {/* Main: Hero stat + feature cards */}
        <div className="grid lg:grid-cols-2 gap-10" data-id="element-2696">
          {/* Left: Hero accuracy stat — dark card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 lg:p-12 relative overflow-hidden flex flex-col justify-between min-h-[320px]" data-id="element-2697">
            {/* Decorative ring */}
            <div className="text-white" data-id="element-2698">
              <AnimatedRing delay={0.3} data-id="element-2699" />
            </div>

            <div className="relative z-10" data-id="element-2700">
              <div className="flex items-center gap-2 mb-3" data-id="element-2701">
                <BadgeCheckIcon className="w-5 h-5 text-blue-400" data-id="element-2702" />
                <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2703">
                  Verified accuracy
                </span>
              </div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex items-baseline gap-1" data-id="element-2704">
                <span className="text-7xl md:text-8xl font-bold text-white tracking-tighter" data-id="element-2705">
                  97-99%
                </span>
                <span className="text-4xl md:text-5xl font-bold text-blue-400" data-id="element-2706">
                  %
                </span>
              </motion.div>
            </div>

            <div className="relative z-10 mt-8" data-id="element-2707">
              <p className="text-slate-400 text-base leading-relaxed max-w-sm" data-id="element-2708">
                Multi-source verification and AI-powered validation across 200M+
                records.
              </p>
            </div>
          </motion.div>

          {/* Right: 3 feature cards stacked */}
          <div className="grid gap-5" data-id="element-2709">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            const colorMap: Record<string, {
              bg: string;
              text: string;
              border: string;
            }> = {
              blue: {
                bg: 'bg-blue-50',
                text: 'text-blue-600',
                border: 'border-blue-100'
              },
              emerald: {
                bg: 'bg-emerald-50',
                text: 'text-emerald-600',
                border: 'border-emerald-100'
              },
              amber: {
                bg: 'bg-amber-50',
                text: 'text-amber-600',
                border: 'border-amber-100'
              }
            };
            const colors = colorMap[feature.color];
            return <motion.div key={index} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.15 + index * 0.1
            }} className="bg-slate-50/60 rounded-2xl border border-slate-100 p-7 cursor-default" data-id="element-2710">
                  <div className={`w-10 h-10 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center mb-4`} data-id="element-2711">
                    <Icon className={`w-5 h-5 ${colors.text}`} data-id="element-2712" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-2713">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed" data-id="element-2714">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}